import { SortOrder } from '@root/src/types/common';
import { useMemo, useCallback } from 'react';
import { useUrlState } from './useUrlState';

export interface UseUrlSortResult {
  sort: { field: string; order: SortOrder };
  setSort: (field: string, order: SortOrder) => void;
  resetSort: () => void;
}

export const useUrlSort = (defaultSort: string): UseUrlSortResult => {
  const [urlState, setUrlState] = useUrlState<{ sort: string }>({ sort: defaultSort });

  const sort = useMemo(() => {
    const raw = urlState.sort || defaultSort;
    const first = raw.split(',')[0];
    const isDesc = first.startsWith('-');
    const field = isDesc ? first.slice(1) : first;
    const order: SortOrder = isDesc ? 'DESC' : 'ASC';
    return { field, order };
  }, [urlState.sort, defaultSort]);

  const setSort = useCallback(
    (field: string, order: SortOrder) => {
      const newSort = order === 'DESC' ? `-${field}` : field;
      if (urlState.sort === newSort) {
        return;
      }
      setUrlState(prev => ({ ...prev, sort: newSort }));
    },
    [setUrlState, urlState.sort],
  );

  const resetSort = useCallback(() => {
    if (urlState.sort === defaultSort) {
      return;
    }
    setUrlState(prev => ({ ...prev, sort: defaultSort }));
  }, [setUrlState, urlState.sort, defaultSort]);

  return { sort, setSort, resetSort };
};
